import { addDays, diffDays, type DateStr } from "./date";

// ---------------------------------------------------------------------------
// Time grid: overlapping timed events inside one day column
// ---------------------------------------------------------------------------

export interface TimedInput {
  key: string;
  /** Minutes from the column's midnight (may be < 0 / > 1440 for events crossing days). */
  start: number;
  end: number;
}

export interface TimedPlacement {
  key: string;
  top: number; // minutes
  height: number; // minutes
  /** Fractions of the column width (0..1). */
  left: number;
  width: number;
  z: number;
}

const MIN_HEIGHT = 15;

/** Events starting within this many minutes of each other sit side by side; later ones cascade. */
const SIDE_BY_SIDE_MIN = 30;

/**
 * Column packing: events that overlap (directly or through a
 * chain) form a cluster and each gets the first free column. Then a cascade:
 * an event stretches to the right edge and later-starting events are drawn on top of
 * it, offset by their column — unless an event to its right starts at about the same
 * time (within SIDE_BY_SIDE_MIN), in which case they share the width side by side.
 */
export function layoutTimed(events: TimedInput[]): TimedPlacement[] {
  const items = events
    .map((e) => ({ ...e, start: Math.max(0, e.start), end: Math.min(1440, Math.max(e.end, e.start + MIN_HEIGHT)) }))
    .filter((e) => e.end > 0 && e.start < 1440)
    .sort((a, b) => a.start - b.start || b.end - a.end || a.key.localeCompare(b.key));

  const out: TimedPlacement[] = [];
  let cluster: typeof items = [];
  let clusterEnd = -1;

  const flush = () => {
    if (!cluster.length) return;
    const columns: Array<typeof items> = [];
    const colOf = new Map<string, number>();
    for (const ev of cluster) {
      let col = columns.findIndex((c) => c[c.length - 1].end <= ev.start);
      if (col === -1) {
        col = columns.length;
        columns.push([]);
      }
      columns[col].push(ev);
      colOf.set(ev.key, col);
    }
    const n = columns.length;
    for (const ev of cluster) {
      const col = colOf.get(ev.key)!;
      // Stop at the first column to the right holding an overlapping event that starts
      // at about the same time (side by side); otherwise run to the right edge (cascade).
      let stop = n;
      for (let c = col + 1; c < n; c++) {
        if (columns[c].some((o) => o.start < ev.end && o.end > ev.start && Math.abs(o.start - ev.start) < SIDE_BY_SIDE_MIN)) {
          stop = c;
          break;
        }
      }
      out.push({ key: ev.key, top: ev.start, height: ev.end - ev.start, left: col / n, width: (stop - col) / n, z: col + 1 });
    }
    cluster = [];
    clusterEnd = -1;
  };

  for (const ev of items) {
    if (cluster.length && ev.start >= clusterEnd) flush();
    cluster.push(ev);
    clusterEnd = Math.max(clusterEnd, ev.end);
  }
  flush();
  return out;
}

// ---------------------------------------------------------------------------
// Rows of days (week all-day strip, month weeks): bars spanning days, stacked
// ---------------------------------------------------------------------------

export interface BarInput {
  key: string;
  start: DateStr; // first day
  endExclusive: DateStr; // day after the last day
  /** Sort hint inside a day: all-day/multi-day first, then by time. */
  order: number;
}

export interface BarPlacement {
  key: string;
  col: number;
  span: number;
  level: number;
  continuesBefore: boolean;
  continuesAfter: boolean;
}

/**
 * Greedy level assignment for bars inside a row of `days` days starting at `rowStart`
 * (month view / all-day strip): longer bars first so they keep the top
 * levels, then single-day items in time order.
 */
export function layoutBars(items: BarInput[], rowStart: DateStr, days: number): BarPlacement[] {
  const rowEnd = addDays(rowStart, days);
  const visible = items
    .filter((i) => i.start < rowEnd && i.endExclusive > rowStart)
    .map((i) => {
      const from = i.start < rowStart ? rowStart : i.start;
      const to = i.endExclusive > rowEnd ? rowEnd : i.endExclusive;
      return { ...i, col: diffDays(rowStart, from), span: Math.max(1, diffDays(from, to)), before: i.start < rowStart, after: i.endExclusive > rowEnd };
    })
    .sort((a, b) => a.col - b.col || b.span - a.span || a.order - b.order || a.key.localeCompare(b.key));

  const levels: boolean[][] = [];
  const out: BarPlacement[] = [];
  for (const bar of visible) {
    let level = 0;
    for (;; level++) {
      if (!levels[level]) levels[level] = Array(days).fill(false);
      let free = true;
      for (let c = bar.col; c < bar.col + bar.span; c++) if (levels[level][c]) free = false;
      if (free) break;
    }
    for (let c = bar.col; c < bar.col + bar.span; c++) levels[level][c] = true;
    out.push({ key: bar.key, col: bar.col, span: bar.span, level, continuesBefore: bar.before, continuesAfter: bar.after });
  }
  return out;
}
